// When a Polaroid's moment and a place's reveal play. Only finishes made on
// this device animate: the finish itself claims the moment (moving the stop
// into seenDone) in the same save that dates the photo, so a moment is decided
// once, where it happened, and a crash before the result costs one animation
// rather than replaying it later out of context. Everything else that became
// done (sync, a first run) is caught up silently.
import type { GameState } from "@aybuben/core/state.ts";

import type { PolaroidMemory, Taken } from "../device/polaroid-memory.ts";
import { doneStops, stopAfter, type Stop } from "./stops.ts";

const add = (xs: readonly string[], more: readonly string[]): string[] => [
	...xs,
	...more.filter((x, i) => !xs.includes(x) && more.indexOf(x) === i),
];

export const firstMemory = (doneNow: readonly string[]): PolaroidMemory => ({
	seenDone: [...doneNow],
	revealed: [...doneNow],
	taken: {},
});

export function atFinish(
	m: PolaroidMemory | null,
	before: GameState,
	after: GameState,
	stops: readonly Stop[],
	taken: Taken,
): { memory: PolaroidMemory; play: string[] } {
	const was = doneStops(before, stops);
	// No memory yet (a first session): what was done before this lesson is old news, this lesson's finish is not.
	const base = m ?? firstMemory(was);
	const newly = doneStops(after, stops).filter((id) => !was.includes(id));
	const play = newly.filter((id) => !base.seenDone.includes(id));
	if (!newly.length) return { memory: base, play };
	const dated = { ...base.taken };
	for (const id of newly) dated[id] ??= taken;
	return { memory: { ...base, seenDone: add(base.seenDone, play), taken: dated }, play };
}

export function catchUp(
	m: PolaroidMemory | null,
	st: GameState,
	stops: readonly Stop[],
): PolaroidMemory {
	const done = doneStops(st, stops);
	if (!m) return firstMemory(done);
	const missed = done.filter((id) => !m.seenDone.includes(id));
	if (!missed.length) return m;
	return { ...m, seenDone: add(m.seenDone, missed), revealed: add(m.revealed, missed) };
}

/** A reveal is owed for a stop this device finished (it has a `taken`) whose reveal has not played. */
export function pendingReveals(
	m: PolaroidMemory,
	stops: readonly Stop[],
): { finished: string; reveal: string | null }[] {
	return m.seenDone
		.filter((id) => !m.revealed.includes(id) && m.taken[id] && stops.some((s) => s.id === id))
		.map((id) => ({ finished: id, reveal: stopAfter(stops, id)?.id ?? null }));
}

export const markRevealed = (m: PolaroidMemory, finishedIds: readonly string[]): PolaroidMemory =>
	finishedIds.every((id) => m.revealed.includes(id))
		? m
		: { ...m, revealed: add(m.revealed, finishedIds) };

/** Callers save only on a change: Learn and Home call catchUp on every render pass. */
export const sameMemory = (a: PolaroidMemory | null, b: PolaroidMemory): boolean =>
	a !== null && JSON.stringify(a) === JSON.stringify(b);

/**
 * One pass of the reveal: catch up what was done elsewhere (silently), then
 * take the reveals owed by finishes made here and mark them. `reveal` is the
 * next stops' ids to un-grey; the caller plays the cue once if it is not empty.
 */
export function revealStep(
	m: PolaroidMemory | null,
	st: GameState,
	stops: readonly Stop[],
): { memory: PolaroidMemory; reveal: string[] } {
	const caught = catchUp(m, st, stops);
	const owed = pendingReveals(caught, stops);
	return {
		memory: markRevealed(
			caught,
			owed.map((o) => o.finished),
		),
		reveal: owed.flatMap((o) => (o.reveal ? [o.reveal] : [])),
	};
}
